import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import {
  TIME_PERIOD_OPTIONS,
  UI_DELAY_BACKGROUND,
} from '../../../config/constants';
import { SHARED_TYPOGRAPHY } from '../../../theme';

/**
 * DashboardHeader Component
 *
 * The main header component for the Prompt Manager Dashboard.
 * Includes title, time period selector, category selector, and refresh button.
 * Uses the design system for consistent styling.
 *
 * @param {string} timePeriod - Currently selected time period
 * @param {string} selectedCategory - Currently selected category
 * @param {Array} categoryAnalytics - Array of available categories
 * @param {boolean} loading - Whether data is currently loading
 * @param {function} onTimePeriodChange - Callback for time period changes
 * @param {function} onCategoryChange - Callback for category changes
 * @param {function} onRefresh - Callback for refresh button click
 * @param {object} overviewData - Overview data object containing period info
 */
const DashboardHeader = ({
  timePeriod,
  customRange,
  selectedCategory,
  categoryAnalytics,
  loading,
  onTimePeriodChange,
  onCategoryChange,
  onRefresh,
  overviewData,
  onSelectClose,
}) => {
  const formatShortDate = iso => {
    if (!iso) return null;
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = d.getMonth() + 1; // 1-12, no leading zero
    const day = d.getDate(); // 1-31, no leading zero
    return `${y}-${m}-${day}`;
  };

  const handleTimePeriodChange = event => {
    const value = event.target.value;
    onTimePeriodChange(event);
    // If custom is selected, always open the dialog
    if (value === 'custom' && onSelectClose) {
      setTimeout(() => {
        onSelectClose();
      }, UI_DELAY_BACKGROUND);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* Main Header Row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h3" component="h1" sx={SHARED_TYPOGRAPHY.h3Bold}>
          Prompt Manager Dashboard
        </Typography>

        {/* Controls */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Time Period Selector */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timePeriod}
              label="Time Period"
              onChange={handleTimePeriodChange}
            >
              {TIME_PERIOD_OPTIONS.map(option => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  onMouseDown={e => {
                    // onMouseDown fires even when clicking an already-selected item
                    // If clicking custom while already selected, open dialog after menu closes
                    if (
                      option.value === 'custom' &&
                      timePeriod === 'custom' &&
                      onSelectClose
                    ) {
                      // Don't prevent default - let menu close normally
                      // But set a flag so we know to open dialog
                      setTimeout(() => {
                        onSelectClose();
                      }, UI_DELAY_BACKGROUND + 50); // Slightly longer delay for menu close
                    }
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Category Selector */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Template Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Template Category"
              onChange={onCategoryChange}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categoryAnalytics &&
                categoryAnalytics.map(category => (
                  <MenuItem key={category.category} value={category.category}>
                    {category.category
                      .replace('_', ' ')
                      .replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Refresh Button */}
          <IconButton onClick={onRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Subtitle/Info */}
      {overviewData && (
        <Typography variant="body2" sx={SHARED_TYPOGRAPHY.bodySecondary}>
          {timePeriod === 'custom' &&
          customRange?.startDate &&
          customRange?.endDate ? (
            <>
              Data for {overviewData.period} (
              {formatShortDate(customRange.startDate)} to{' '}
              {formatShortDate(customRange.endDate)}) • Last updated:{' '}
              {new Date().toLocaleTimeString()}
            </>
          ) : (
            <>
              Data for {overviewData.period} • Last updated:{' '}
              {new Date().toLocaleTimeString()}
            </>
          )}
        </Typography>
      )}
    </Box>
  );
};

export default DashboardHeader;
